/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  getSienge(contactId) {
    return axios.get(`${this.url}/${contactId}/sienge`);
  }

  siengeAction(contactId, action, data) {
    return axios.post(`${this.url}/${contactId}/sienge/${action}`, data);
  }

  getAttachments(contactId, { page = 1, fileType } = {}) {
    let requestURL = `${this.url}/${contactId}/attachments?page=${page}`;
    if (fileType) {
      requestURL += `&file_type=${fileType}`;
    }
    return axios.get(requestURL);
  }

  getRecordings(contactId) {
    return axios.get(`${this.url}/${contactId}/recordings`);
  }

  transcribeRecording(contactId, recordingType, recordingId) {
    return axios.post(`${this.url}/${contactId}/recordings/transcribe`, {
      recording_type: recordingType,
      recording_id: recordingId,
    });
  }

  generateRecordingAta(contactId, recordingType, recordingId) {
    return axios.post(`${this.url}/${contactId}/recordings/ata`, {
      recording_type: recordingType,
      recording_id: recordingId,
    });
  }

  analyzeRecording(contactId, recordingType, recordingId, analysisType) {
    return axios.post(`${this.url}/${contactId}/recordings/analyze`, {
      recording_type: recordingType,
      recording_id: recordingId,
      analysis_type: analysisType,
    });
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL);
  }

  active(page = 1, sortAttr = 'name') {
    let requestURL = `${this.url}/active?${buildContactParams(page, sortAttr)}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file, labels = []) {
    const formData = new FormData();
    formData.append('import_file', file);
    // Append each label as a separate form field for Rails array handling
    labels.forEach(label => {
      formData.append('labels[]', label);
    });
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  cnpjLookup(cnpj, contactId) {
    return axios.get(`${this.url}/cnpj_lookup`, {
      params: { cnpj, contact_id: contactId },
    });
  }

  resetAiMemory(contactId) {
    return axios.post(`${this.url}/${contactId}/reset_ai_memory`);
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  bulkAssignLabels(payload) {
    return axios.post(`${this.url}/bulk_assign_labels`, payload);
  }

  normalizePhoneNumbers() {
    return axios.post(`${this.url}/normalize_phone_numbers`);
  }

  getDuplicates() {
    return axios.get(`${this.url}/duplicates`);
  }
}

export default new ContactAPI();
